import { useMemo } from "react";

import { type BusinessTypeOptions } from "./uiParts/BusinessTypeSelector";
import type { BusinessTypeFilterOption, FormValues } from "./types";

const filterByBusinessTypes = (
  options: BusinessTypeFilterOption[],
  businessTypeIds: string[],
): BusinessTypeFilterOption[] => {
  if (businessTypeIds.length === 0) return options;
  const set = new Set(businessTypeIds);
  return options.filter((option) => option.businessTypeIds.some((id) => set.has(id)));
};

export const useBusinessTypeFiltering = (params: {
  businessTypeOptions: BusinessTypeOptions[];
  employmentTypeOptions: BusinessTypeFilterOption[];
  preferentialConditionsOptions: BusinessTypeFilterOption[];
  occupationOptions: BusinessTypeFilterOption[];
  facilityTypeOptions: BusinessTypeFilterOption[];
  customerTypeOptions: BusinessTypeFilterOption[];
  featureOptions: BusinessTypeFilterOption[];
  experienceOptions: BusinessTypeFilterOption[];
  workDescriptionOptions: BusinessTypeFilterOption[];
  userCredentialOptions: BusinessTypeFilterOption[];
  formValues: FormValues;
  onBusinessTypesChange: (next: FormValues) => void;
}) => {
  const {
    businessTypeOptions,
    employmentTypeOptions,
    preferentialConditionsOptions,
    occupationOptions,
    facilityTypeOptions,
    customerTypeOptions,
    featureOptions,
    experienceOptions,
    workDescriptionOptions,
    userCredentialOptions,
    formValues,
    onBusinessTypesChange,
  } = params;

  const businessTypeValueToIdMap = useMemo(() => {
    const map = new Map<string, string>();
    businessTypeOptions.forEach((group) => {
      group.choices.forEach((choice) => {
        map.set(choice.value, choice.id);
      });
    });
    return map;
  }, [businessTypeOptions]);

  const applyFilter = (options: BusinessTypeFilterOption[]) => {
    const selectedBusinessTypeIds = formValues["q[business_type][]"].map(
      (value) => businessTypeValueToIdMap.get(value) ?? value,
    );
    return filterByBusinessTypes(options, selectedBusinessTypeIds);
  };

  const handleBusinessTypesChange = (businessTypes: string[]) => {
    const selectedBusinessTypeIds = businessTypes.map((v) => businessTypeValueToIdMap.get(v) ?? v);

    if (selectedBusinessTypeIds.length === 0) {
      onBusinessTypesChange({ ...formValues, "q[business_type][]": businessTypes });
      return;
    }

    const getAllowedValuesSet = (options: BusinessTypeFilterOption[]) =>
      new Set(
        filterByBusinessTypes(options, selectedBusinessTypeIds).map((option) => option.value),
      );

    const cleanse = (values: string[], options: BusinessTypeFilterOption[]) =>
      values.filter((value) => getAllowedValuesSet(options).has(value));

    const newFormValues: FormValues = {
      ...formValues,
      "q[business_type][]": businessTypes,
      "q[employment][]": cleanse(formValues["q[employment][]"], employmentTypeOptions),
      "q[occupation][]": cleanse(formValues["q[occupation][]"], occupationOptions),
      "q[facility_type][]": cleanse(formValues["q[facility_type][]"], facilityTypeOptions),
      "q[customer_type][]": cleanse(formValues["q[customer_type][]"], customerTypeOptions),
      "q[feature][]": cleanse(formValues["q[feature][]"], featureOptions),
      "q[experience][]": cleanse(formValues["q[experience][]"], experienceOptions),
      "q[work_description][]": cleanse(formValues["q[work_description][]"], workDescriptionOptions),
      "q[user_credential][]": cleanse(formValues["q[user_credential][]"], userCredentialOptions),
      "q[salon_tour_flag]": getAllowedValuesSet(preferentialConditionsOptions).has(
        "salon_tour_flag",
      )
        ? formValues["q[salon_tour_flag]"]
        : false,
      "q[client_end_up_close_flag]": getAllowedValuesSet(preferentialConditionsOptions).has(
        "client_end_up_close_flag",
      )
        ? formValues["q[client_end_up_close_flag]"]
        : false,
    };

    onBusinessTypesChange(newFormValues);
  };

  return {
    filteredEmploymentTypeOptions: applyFilter(employmentTypeOptions),
    filteredPreferentialConditionsOptions: applyFilter(preferentialConditionsOptions),
    filteredOccupationOptions: applyFilter(occupationOptions),
    filteredFacilityTypeOptions: applyFilter(facilityTypeOptions),
    filteredCustomerTypeOptions: applyFilter(customerTypeOptions),
    filteredFeatureOptions: applyFilter(featureOptions),
    filteredExperienceOptions: applyFilter(experienceOptions),
    filteredWorkDescriptionOptions: applyFilter(workDescriptionOptions),
    filteredUserCredentialOptions: applyFilter(userCredentialOptions),
    handleBusinessTypesChange,
  };
};
